import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, MapPin, Clock, Users, FileText, Download, CheckCircle2, Music, User, Star, MessageCircle, Mail, Camera, Video, Play, X as XIcon } from 'lucide-react';
import { InstallPWA } from './components/InstallPWA';
import { EventSpacesDisplay } from './components/EventSpaces';
import { ClientWelcomePage } from './components/ClientWelcomePage';
import { API_URL } from './config';
import logger from '@/utils/logger';

const API = API_URL;

// Extrae el track id de Spotify de una petición (soporta spotify_id, spotify:track:ID y URL open.spotify.com)
const getSpotifyTrackId = (peticion) => {
  if (!peticion) return null;
  if (peticion.spotify_id) return peticion.spotify_id;
  const uri = peticion.spotify_uri || peticion.spotify_url || '';
  if (!uri) return null;
  let m = uri.match(/spotify:track:([A-Za-z0-9]+)/);
  if (m) return m[1];
  m = uri.match(/open\.spotify\.com\/track\/([A-Za-z0-9]+)/);
  if (m) return m[1];
  return null;
};

// Extrae el video id de una URL de YouTube
const getYoutubeId = (url) => {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

// Determina si una petición es reproducible y de qué tipo
const getPeticionPlayback = (peticion) => {
  const spId = getSpotifyTrackId(peticion);
  if (spId) return { type: 'spotify', id: spId };
  const ytId = getYoutubeId(peticion.youtube_url);
  if (ytId) return { type: 'youtube', id: ytId };
  if (peticion.audio_file_id) return { type: 'audio', id: peticion.audio_file_id };
  return null;
};

export const ClientPortal = () => {
  const [accessCode, setAccessCode] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [presupuestos, setPresupuestos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('info');
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Estados para subida de audio
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [audioNota, setAudioNota] = useState('');
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);
  
  // Estado para página de bienvenida
  const [showWelcome, setShowWelcome] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // Estado para reproducir peticiones en el portal (índice de la petición abierta)
  const [playingPeticionKey, setPlayingPeticionKey] = useState(null);

  // Campos editables del cliente
  const [preferencias, setPreferencias] = useState({
    gustos_musicales: '',
    canciones_prohibidas: '',
    canciones_imperdibles: '',
    hora_loca_cancion: '',
    playlist_spotify: '',
    playlist_spotify_urls: [],
    client_notas: ''
  });

  // Detectar si está en standalone y si debe mostrar bienvenida
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Verificar si ya está autenticado
    const savedCode = localStorage.getItem('client_access_code');
    
    // Mostrar bienvenida si NO está en standalone Y NO está autenticado
    if (!standalone && !savedCode) {
      setShowWelcome(true);
    }
  }, []);

  // Check if already logged in or if code is in URL
  useEffect(() => {
    const attemptLogin = async () => {
      // Primero, intentar obtener el código de la URL
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromUrl = urlParams.get('code');
      
      logger.log('🔍 ClientPortal useEffect:', { codeFromUrl, hasLocalStorage: !!localStorage.getItem('client_access_code') });
      
      if (codeFromUrl) {
        // Si hay código en la URL, usarlo para login automático
        logger.log('✅ Código encontrado en URL:', codeFromUrl);
        setAccessCode(codeFromUrl);
        await handleAutoLogin(codeFromUrl);
      } else {
        // Si no hay código en URL, verificar localStorage
        const savedCode = localStorage.getItem('client_access_code');
        if (savedCode) {
          logger.log('✅ Código encontrado en localStorage:', savedCode);
          await handleAutoLogin(savedCode);
        } else {
          logger.log('ℹ️ No hay código en URL ni en localStorage');
        }
      }
    };
    
    attemptLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: handleAutoLogin
  }, []);

  const handleAutoLogin = async (code) => {
    logger.log('🔐 handleAutoLogin llamado con código:', code);
    setLoading(true);
    setError(''); // Limpiar errores previos
    
    try {
      const url = `${API}/client/event/${code}`;
      logger.log('📡 Fetching:', url);
      
      const response = await fetch(url);
      logger.log('📊 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        logger.log('✅ Data recibida:', { nombre: data.nombre_novios, peticiones: data.peticiones?.length });
        
        setEventData(data);
        setIsLoggedIn(true);
        setAccessCode(code);
        
        // Guardar código en localStorage para futuras visitas
        localStorage.setItem('client_access_code', code);
        logger.log('💾 Código guardado en localStorage');
        
        // Cargar preferencias del cliente
        setPreferencias({
          gustos_musicales: data.gustos_musicales || '',
          canciones_prohibidas: data.canciones_prohibidas || '',
          canciones_imperdibles: data.canciones_imperdibles || '',
          hora_loca_cancion: data.hora_loca_cancion || '',
          playlist_spotify: data.playlist_spotify || '',
          playlist_spotify_urls: data.playlist_spotify_urls || [],
          client_notas: data.client_notas || ''
        });
        
        loadPresupuestos(code);
        loadContratos(code);
      } else {
        const errorData = await response.text();
        console.error('❌ Response error:', response.status, errorData);
        localStorage.removeItem('client_access_code');
        setError('Código de acceso inválido');
      }
    } catch (err) {
      console.error('❌ Auto-login error:', err);
      localStorage.removeItem('client_access_code');
      setError('Error al cargar el evento');
    } finally {
      setLoading(false);
      logger.log('🏁 handleAutoLogin finalizado');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/client/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ access_code: accessCode })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Código de acceso inválido');
      }

      const loginData = await response.json();
      
      // Load full event data
      const eventResponse = await fetch(`${API}/client/event/${accessCode}`);
      const eventInfo = await eventResponse.json();
      
      setEventData(eventInfo);
      setIsLoggedIn(true);
      localStorage.setItem('client_access_code', accessCode);
      
      // Cargar preferencias del cliente
      setPreferencias({
        gustos_musicales: eventInfo.gustos_musicales || '',
        canciones_prohibidas: eventInfo.canciones_prohibidas || '',
        canciones_imperdibles: eventInfo.canciones_imperdibles || '',
        hora_loca_cancion: eventInfo.hora_loca_cancion || '',
        playlist_spotify: eventInfo.playlist_spotify || '',
        playlist_spotify_urls: eventInfo.playlist_spotify_urls || [],
        client_notas: eventInfo.client_notas || ''
      });
      
      // Load budgets and contracts
      loadPresupuestos(accessCode);
      loadContratos(accessCode);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPresupuestos = async (code) => {
    try {
      const response = await fetch(`${API}/client/event/${code}/presupuestos`);
      if (response.ok) {
        const data = await response.json();
        setPresupuestos(data);
      }
    } catch (err) {
      console.error('Error loading presupuestos:', err);
    }
  };

  const loadContratos = async (code) => {
    try {
      const response = await fetch(`${API}/client/event/${code}/contratos`);
      if (response.ok) {
        const data = await response.json();
        setContratos(data.contratos || []);
      }
    } catch (err) {
      console.error('Error loading contratos:', err);
    }
  };

  const handleFirmarContrato = async (contratoId, clienteNombre) => {
    if (!window.confirm('¿Estás seguro de que deseas firmar este contrato? Esta acción es irreversible.')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await fetch(`${API}/client/contratos/${contratoId}/firmar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          acepto_terminos: true,
          firmante_nombre: clienteNombre
        })
      });
      
      if (response.ok) {
        alert('¡Contrato firmado exitosamente!');
        loadContratos(accessCode);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'No se pudo firmar el contrato'}`);
      }
    } catch (err) {
      alert('Error al firmar el contrato');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setEventData(null);
    setPresupuestos([]);
    setContratos([]);
    setAccessCode('');
    setPreferencias({
      gustos_musicales: '',
      canciones_prohibidas: '',
      canciones_imperdibles: '',
      hora_loca_cancion: '',
      playlist_spotify: '',
      playlist_spotify_urls: [],
      client_notas: ''
    });
    localStorage.removeItem('client_access_code');
  };

  const handleSavePreferencias = async () => {
    setSaving(true);
    try {
      const code = localStorage.getItem('client_access_code');
      const response = await fetch(`${API}/client/event/${code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferencias)
      });

      if (!response.ok) {
        throw new Error('Error al guardar preferencias');
      }

      alert('✅ Preferencias guardadas correctamente');
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al guardar las preferencias');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande (máximo 10MB)');
      return;
    }

    setUploadingDoc(true);
    try {
      const code = localStorage.getItem('client_access_code');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', 'cliente');

      const response = await fetch(`${API}/client/event/${code}/documentos/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al subir documento');
      }

      alert('✅ Documento subido correctamente');
      
      // Reload event data
      handleAutoLogin(code);
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al subir el documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleUploadAudio = async () => {
    if (!selectedAudioFile) {
      alert('Selecciona un archivo primero');
      return;
    }

    setUploadingAudio(true);
    setAudioUploadProgress(0);

    try {
      const code = localStorage.getItem('client_access_code');
      const formData = new FormData();
      formData.append('file', selectedAudioFile);
      formData.append('playlist_id', eventData.id);
      formData.append('nota', audioNota);

      const xhr = new XMLHttpRequest();

      // Progress tracking
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setAudioUploadProgress(percentComplete);
        }
      });

      // Success handler
      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          alert('✅ Archivo de audio subido correctamente y agregado a peticiones');
          
          // Reset form
          setSelectedAudioFile(null);
          setAudioNota('');
          setAudioUploadProgress(0);
          
          // Clear file input
          const fileInput = document.getElementById('audio-file');
          if (fileInput) fileInput.value = '';
          
          // Reload event data to show the new petition
          try {
            const eventResponse = await fetch(`${API}/client/event/${code}`);
            const eventInfo = await eventResponse.json();
            setEventData(eventInfo);
            logger.log('✅ Event data reloaded, peticiones:', eventInfo.peticiones?.length);
          } catch (error) {
            console.error('Error reloading event data:', error);
          }
          
          setUploadingAudio(false);
        } else {
          throw new Error('Error al subir archivo');
        }
      });

      // Error handler
      xhr.addEventListener('error', () => {
        throw new Error('Error de red al subir archivo');
      });

      xhr.open('POST', `${API}/audio/upload`);
      xhr.send(formData);
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al subir el archivo de audio');
      setUploadingAudio(false);
      setAudioUploadProgress(0);
    } finally {
      // Note: setUploadingAudio(false) is handled in success callback
      // to prevent premature reset during upload
      setTimeout(() => {
        setUploadingAudio(false);
      }, 500);
    }
  };

  const downloadPresupuestoPDF = async (presupuestoId) => {
    try {
      // Use public endpoint for PDF download
      const response = await fetch(`${API}/presupuestos/public/${presupuestoId}/pdf`);

      if (!response.ok) {
        alert('Error al descargar el presupuesto');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presupuesto_${presupuestoId.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al descargar el presupuesto');
    }
  };

  // Welcome Screen (para navegador, no standalone)
  if (showWelcome && !isLoggedIn) {
    return <ClientWelcomePage onDismiss={() => setShowWelcome(false)} />;
  }

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src="/logo.png" alt="Luis Herrera Eventos" className="h-32 w-auto" />
            </div>
            <CardTitle className="text-2xl text-white">Portal del Cliente</CardTitle>
            <p className="text-slate-400 mt-2">Luis Herrera Eventos DJ TEAM</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-slate-300">Código de Acceso</Label>
                <Input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="EVENT-2024-XXXXXXXX"
                  className="bg-slate-700 border-slate-600 text-white mt-2"
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Ingresa el código de acceso proporcionado por tu DJ
                </p>
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-700 text-red-400 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white"
              >
                {loading ? 'Accediendo...' : 'Acceder'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Portal Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Luis Herrera Eventos" className="h-32 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Music className="w-6 h-6 text-sky-500" />
                  {eventData?.nombre_novios}
                </h1>
                <p className="text-slate-400 text-sm mt-1">Portal del Cliente</p>
              </div>
            </div>
            <div className="flex gap-2">
              <InstallPWA isPortal={true} />
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: 'info', label: 'Información', icon: Calendar },
              { id: 'preferencias', label: 'Mis Preferencias', icon: Music },
              { id: 'peticiones', label: 'Peticiones', icon: MessageCircle },
              { id: 'contratos', label: 'Presupuestos y Contratos', icon: FileText },
              { id: 'documentos', label: 'Documentos', icon: Download },
              { id: 'resenas', label: 'Reseñas', icon: Star }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-sky-500 text-sky-400'
                      : 'border-transparent text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Información del Evento */}
        {activeTab === 'info' && (
          <div className="space-y-6">
            {(() => {
              const f = eventData?.fecha_evento;
              const fd = f ? new Date(f) : null;
              const eventPast = (eventData?.estado || '').toLowerCase() === 'completado' ||
                (fd && !isNaN(fd.getTime()) && fd < new Date(new Date().toDateString()));
              if (!eventPast || !eventData?.recuerdo_visible_cliente) return null;
              return (
                <Card className="bg-gradient-to-br from-rose-900/40 to-slate-800 border-rose-700/50" data-testid="recuerdo-card">
                  <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                        <Music className="w-5 h-5 text-rose-400" />
                        Vuestro Recuerdo Musical
                      </h3>
                      <p className="text-sm text-slate-300 mt-1">
                        Descargad el PDF con todas las canciones que sonaron en vuestro gran día.
                      </p>
                    </div>
                    <Button
                      data-testid="recuerdo-download-btn"
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/recuerdo/client/${accessCode}`);
                          if (!res.ok) {
                            throw new Error(res.status === 425 ? 'pendiente' : 'error');
                          }
                          const blob = await res.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `Recuerdo_${(eventData?.nombre_novios || 'novios').replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          alert(err.message === 'pendiente'
                            ? 'El recuerdo estará disponible después de vuestro gran día.'
                            : 'No se pudo generar el recuerdo. Inténtalo más tarde.');
                        }
                      }}
                      className="bg-rose-600 hover:bg-rose-500 text-white whitespace-nowrap"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Recuerdo
                    </Button>
                  </CardContent>
                </Card>
              );
            })()}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-sky-500" />
                  Detalles del Evento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-sky-400 mt-1" />
                    <div>
                      <div className="text-sm text-slate-400">Fecha</div>
                      <div className="text-white font-medium">{eventData?.fecha_evento || 'No especificada'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-sky-400 mt-1" />
                    <div>
                      <div className="text-sm text-slate-400">Lugar</div>
                      <div className="text-white font-medium">{eventData?.lugar_evento || 'No especificado'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-sky-400 mt-1" />
                    <div>
                      <div className="text-sm text-slate-400">Horario</div>
                      <div className="text-white font-medium">
                        {eventData?.horario_inicio || '--:--'} - {eventData?.horario_fin || '--:--'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-sky-400 mt-1" />
                    <div>
                      <div className="text-sm text-slate-400">Invitados</div>
                      <div className="text-white font-medium">{eventData?.numero_invitados || 'No especificado'}</div>
                    </div>
                  </div>

                  {eventData?.nombre_dj && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-sky-400 mt-1" />
                      <div>
                        <div className="text-sm text-slate-400">DJ</div>
                        <div className="text-white font-medium">{eventData.nombre_dj}</div>
                      </div>
                    </div>
                  )}

                  {eventData?.nombre_asistente && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-sky-400 mt-1" />
                      <div>
                        <div className="text-sm text-slate-400">Asistente</div>
                        <div className="text-white font-medium">{eventData.nombre_asistente}</div>
                      </div>
                    </div>
                  )}
                </div>

                {eventData?.espacios_evento && eventData.espacios_evento.length > 0 && (
                  <div className="pt-4 border-t border-slate-700" data-testid="client-portal-spaces">
                    <div className="text-sm text-slate-400 mb-2 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-sky-400" /> Espacios del evento
                    </div>
                    <EventSpacesDisplay espacios={eventData.espacios_evento} />
                  </div>
                )}

                {eventData?.estado && (
                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">Estado:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        eventData.estado === 'confirmado' ? 'bg-green-900/50 text-green-300' :
                        eventData.estado === 'completado' ? 'bg-blue-900/50 text-blue-300' :
                        'bg-yellow-900/50 text-yellow-300'
                      }`}>
                        {eventData.estado.charAt(0).toUpperCase() + eventData.estado.slice(1)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Servicios Contratados */}
            {eventData?.servicios_contratados && eventData.servicios_contratados.length > 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Servicios Contratados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {eventData.servicios_contratados.map((servicio, index) => (
                      <div key={index} className="bg-slate-700/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span className="text-white font-medium">{servicio.nombre}</span>
                        </div>
                        {servicio.notas && (
                          <p className="text-sm text-slate-400 mt-1 ml-6">{servicio.notas}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enlaces de Descarga */}
            {(eventData?.link_fotomaton || eventData?.link_360) && (
              <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    📸 Descarga tus Fotos y Videos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {eventData.link_fotomaton && (
                    <a
                      href={eventData.link_fotomaton}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <div className="bg-slate-700/50 hover:bg-slate-700 p-4 rounded-lg transition-colors border border-purple-600/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-purple-600 rounded-full p-2">
                              <Camera className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold">Fotos del Fotomatón</h4>
                              <p className="text-slate-400 text-sm">Haz clic para descargar</p>
                            </div>
                          </div>
                          <Download className="w-5 h-5 text-purple-400" />
                        </div>
                      </div>
                    </a>
                  )}
                  
                  {eventData.link_360 && (
                    <a
                      href={eventData.link_360}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <div className="bg-slate-700/50 hover:bg-slate-700 p-4 rounded-lg transition-colors border border-purple-600/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-purple-600 rounded-full p-2">
                              <Video className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold">Videos 360°</h4>
                              <p className="text-slate-400 text-sm">Haz clic para descargar</p>
                            </div>
                          </div>
                          <Download className="w-5 h-5 text-purple-400" />
                        </div>
                      </div>
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Botón WhatsApp */}
            <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-green-600 rounded-full p-4">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">¿Tienes alguna duda?</h3>
                    <p className="text-slate-300 text-sm mb-3">
                      Contáctanos directamente por WhatsApp
                    </p>
                    <a
                      href="https://wa.me/34667472074?text=Hola, tengo una consulta sobre mi evento."
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-green-600 hover:bg-green-700 text-white">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Escribir por WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email de contacto */}
            <Card className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border border-cyan-700/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-cyan-600 rounded-full p-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">¿Prefieres escribirnos?</h3>
                    <p className="text-slate-300 text-sm mb-3">
                      También puedes contactarnos por email
                    </p>
                    <a
                      href="mailto:info@luisherreraeventos.com"
                      className="inline-block"
                    >
                      <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                        <Mail className="w-4 h-4 mr-2" />
                        info@luisherreraeventos.com
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mis Preferencias */}
        {activeTab === 'preferencias' && (
          <div className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Music className="w-5 h-5 text-sky-500" />
                  Mis Preferencias Musicales
                </CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Completa la información para que podamos crear la lista musical perfecta para tu evento
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Gustos Musicales</Label>
                  <textarea
                    className="w-full mt-2 bg-slate-900 border-slate-700 text-white rounded-md p-3 min-h-[80px]"
                    placeholder="Ej: Reggaeton, Salsa, Bachata, Pop..."
                    value={preferencias.gustos_musicales}
                    onChange={(e) => setPreferencias({...preferencias, gustos_musicales: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Estilos de música que te gustaría tener en tu evento (separados por comas)
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Canciones o Artistas Prohibidos</Label>
                  <textarea
                    className="w-full mt-2 bg-slate-900 border-slate-700 text-white rounded-md p-3 min-h-[80px]"
                    placeholder="Ej: Despacito, Macarena, Luis Fonsi..."
                    value={preferencias.canciones_prohibidas}
                    onChange={(e) => setPreferencias({...preferencias, canciones_prohibidas: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Canciones o artistas que NO quieres en tu evento
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Canciones o Artistas Imperdibles</Label>
                  <textarea
                    className="w-full mt-2 bg-slate-900 border-slate-700 text-white rounded-md p-3 min-h-[80px]"
                    placeholder="Ej: Vivir mi vida, Marc Anthony, Bailando..."
                    value={preferencias.canciones_imperdibles}
                    onChange={(e) => setPreferencias({...preferencias, canciones_imperdibles: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Canciones o artistas que DEBEN estar sí o sí
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Canción Inicio Hora Loca</Label>
                  <Input
                    type="text"
                    className="bg-slate-900 border-slate-700 text-white mt-2"
                    placeholder="Ej: Gasolina - Daddy Yankee"
                    value={preferencias.hora_loca_cancion}
                    onChange={(e) => setPreferencias({...preferencias, hora_loca_cancion: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Canción especial para iniciar la hora loca
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Playlists de Spotify (opcional)</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Añade una o varias playlists de Spotify con las canciones que te gustan
                  </p>
                  
                  {/* Lista de playlists */}
                  <div className="space-y-2 mb-3">
                    {preferencias.playlist_spotify_urls && preferencias.playlist_spotify_urls.map((item, index) => (
                      <div key={item.id || index} className="flex items-center gap-2 bg-slate-800 p-2 rounded border border-slate-700">
                        <span className="bg-cyan-600/30 text-cyan-300 px-2 py-1 rounded text-xs font-semibold">
                          {item.label}
                        </span>
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 text-sm flex-1 truncate"
                        >
                          {item.url}
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            const newUrls = preferencias.playlist_spotify_urls.filter((_, i) => i !== index);
                            setPreferencias({...preferencias, playlist_spotify_urls: newUrls});
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Formulario para añadir nueva playlist */}
                  <div className="space-y-2">
                    <Input
                      type="text"
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder="Etiqueta (ej: Música que nos gusta, Cena, Baile)"
                      id="new-playlist-label"
                    />
                    <Input
                      type="text"
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder="https://open.spotify.com/playlist/..."
                      id="new-playlist-url"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const label = document.getElementById('new-playlist-label').value.trim();
                        const url = document.getElementById('new-playlist-url').value.trim();
                        
                        if (!label || !url) {
                          alert('Por favor completa la etiqueta y la URL');
                          return;
                        }
                        
                        if (!url.includes('spotify.com')) {
                          alert('Por favor ingresa una URL válida de Spotify');
                          return;
                        }
                        
                        const newPlaylist = {
                          id: Date.now().toString(),
                          label: label,
                          url: url
                        };
                        
                        setPreferencias({
                          ...preferencias,
                          playlist_spotify_urls: [...(preferencias.playlist_spotify_urls || []), newPlaylist]
                        });
                        
                        // Limpiar campos
                        document.getElementById('new-playlist-label').value = '';
                        document.getElementById('new-playlist-url').value = '';
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                      + Añadir Playlist
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Notas y Comentarios</Label>
                  <textarea
                    className="w-full mt-2 bg-slate-900 border-slate-700 text-white rounded-md p-3 min-h-[120px]"
                    placeholder="Escribe aquí cualquier comentario, petición especial o detalle que quieras que sepamos..."
                    value={preferencias.client_notas}
                    onChange={(e) => setPreferencias({...preferencias, client_notas: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Espacio libre para cualquier comentario o petición especial
                  </p>
                </div>

                <Button
                  onClick={handleSavePreferencias}
                  disabled={saving}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                >
                  {saving ? 'Guardando...' : '💾 Guardar Preferencias'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Peticiones de Canciones */}
        {activeTab === 'peticiones' && (
          <div className="space-y-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-pink-500" />
                  Peticiones de Canciones
                </CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Solicita canciones para tu evento o sube archivos de audio personalizados
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Subir Audio Personalizado */}
                <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Music className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-2">
                        Subir Canción Personalizada
                      </h3>
                      <p className="text-slate-400 text-sm mb-4">
                        ¿Tienes una canción especial que quieres que suene en tu evento? 
                        Sube un archivo MP3 (máximo 18MB) y agrégalo a la lista.
                      </p>
                      <ul className="list-disc list-inside text-slate-400 text-sm space-y-1 mb-4">
                        <li>Solo archivos MP3</li>
                        <li>Tamaño máximo: 18MB</li>
                        <li>Puedes agregar una nota (ej: "Entrada", "Primera baile")</li>
                      </ul>
                    </div>
                  </div>

                  {/* Upload Form */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="audio-file" className="text-white mb-2">
                        Seleccionar archivo MP3
                      </Label>
                      <Input
                        id="audio-file"
                        type="file"
                        accept="audio/mpeg,audio/mp3"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validar formato
                            if (!file.type.includes('audio/mpeg') && !file.name.toLowerCase().endsWith('.mp3')) {
                              alert('Solo se permiten archivos MP3');
                              e.target.value = '';
                              return;
                            }
                            // Validar tamaño
                            if (file.size > 18 * 1024 * 1024) {
                              alert(`Archivo muy grande. Máximo 18MB (tamaño: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
                              e.target.value = '';
                              return;
                            }
                            setSelectedAudioFile(file);
                          }
                        }}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                      {selectedAudioFile && (
                        <p className="text-sm text-slate-400 mt-2">
                          📁 {selectedAudioFile.name} ({(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="audio-nota" className="text-white mb-2">
                        Nota para el DJ (opcional)
                      </Label>
                      <Input
                        id="audio-nota"
                        value={audioNota}
                        onChange={(e) => setAudioNota(e.target.value)}
                        placeholder="Ej: Primera baile, Entrada, Regalo madres..."
                        className="bg-slate-700 border-slate-600 text-white"
                        maxLength={100}
                      />
                    </div>

                    <Button
                      onClick={handleUploadAudio}
                      disabled={!selectedAudioFile || uploadingAudio}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {uploadingAudio ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Subiendo... {audioUploadProgress}%
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Subir Archivo
                        </>
                      )}
                    </Button>

                    {uploadingAudio && (
                      <div className="w-full bg-slate-600 rounded-full h-2">
                        <div
                          className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${audioUploadProgress}%` }}
                        />
                      </div>
                    )}

                    <p className="text-xs text-yellow-300">
                      ⚠️ Al subir este archivo, confirmas que tienes los derechos necesarios para su uso.
                    </p>
                  </div>
                </div>

                {/* Lista de Peticiones Actuales */}
                {eventData?.peticiones && eventData.peticiones.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3">
                      Peticiones Actuales ({eventData.peticiones.length})
                    </h3>
                    <div className="space-y-3">
                      {eventData.peticiones.map((peticion, index) => (
                        <div
                          key={index}
                          className="bg-slate-900/50 p-4 rounded-lg border border-slate-700"
                        >
                          <div className="flex items-start gap-3">
                            {peticion.is_custom_upload ? (
                              <div className="flex items-center gap-2">
                                <Music className="w-4 h-4 text-purple-400" />
                                <span className="text-xs bg-purple-900/30 text-purple-300 px-2 py-0.5 rounded">
                                  💾 Tu archivo
                                </span>
                              </div>
                            ) : (
                              <Music className="w-4 h-4 text-pink-400" />
                            )}
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <h4 className="text-white font-semibold truncate" title={peticion.title || peticion.titulo}>
                                {peticion.title || peticion.titulo}
                              </h4>
                              <p className="text-slate-400 text-sm truncate">
                                {peticion.artist || peticion.artista}
                              </p>
                              {peticion.momento && (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-900/40 border border-amber-600/50 text-amber-300 text-[11px] font-medium"
                                    title={`Momento del evento: ${peticion.momento}`}
                                  >
                                    <span aria-hidden="true">🎯</span>
                                    {peticion.momento}
                                  </span>
                                </div>
                              )}
                              {peticion.nota && (
                                <div className="mt-2 p-2 bg-slate-800 rounded">
                                  <p className="text-slate-300 text-sm">
                                    📝 {peticion.nota}
                                  </p>
                                </div>
                              )}
                              {(() => {
                                const playback = getPeticionPlayback(peticion);
                                if (!playback) return null;
                                const key = peticion.id || `pet-${index}`;
                                const isOpen = playingPeticionKey === key;
                                return (
                                  <div className="mt-3">
                                    <Button
                                      size="sm"
                                      onClick={() => setPlayingPeticionKey(isOpen ? null : key)}
                                      className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                                      data-testid={`peticion-play-btn-${index}`}
                                    >
                                      {isOpen ? (
                                        <><XIcon className="w-4 h-4 mr-1.5" /> Cerrar</>
                                      ) : (
                                        <><Play className="w-4 h-4 mr-1.5" /> Escuchar</>
                                      )}
                                    </Button>
                                    {isOpen && (
                                      <div className="mt-2" data-testid={`peticion-player-${index}`}>
                                        {playback.type === 'spotify' && (
                                          <iframe
                                            title={`spotify-${key}`}
                                            src={`https://open.spotify.com/embed/track/${playback.id}`}
                                            width="100%"
                                            height="152"
                                            frameBorder="0"
                                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                            loading="lazy"
                                            className="rounded-lg"
                                          />
                                        )}
                                        {playback.type === 'youtube' && (
                                          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                              title={`youtube-${key}`}
                                              src={`https://www.youtube.com/embed/${playback.id}`}
                                              className="absolute inset-0 w-full h-full rounded-lg"
                                              frameBorder="0"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                            />
                                          </div>
                                        )}
                                        {playback.type === 'audio' && (
                                          <audio
                                            controls
                                            className="w-full"
                                            src={`${API}/audio/${playback.id}`}
                                            data-testid={`peticion-audio-${index}`}
                                          >
                                            Tu navegador no soporta la reproducción de audio.
                                          </audio>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!eventData?.peticiones || eventData.peticiones.length === 0) && (
                  <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-lg">
                    <Music className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                    <p className="text-slate-400">Aún no hay peticiones para este evento</p>
                    <p className="text-slate-500 text-sm mt-2">
                      Sube tu primera canción personalizada usando el formulario de arriba
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contratos y Presupuestos */}
        {activeTab === 'contratos' && (
          <div className="space-y-6">
            {/* SECCIÓN PRESUPUESTOS */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-500" />
                  Presupuestos
                </CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Revisa los presupuestos de tu evento y acepta o rechaza según corresponda
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {presupuestos.length === 0 ? (
                  <div className="text-center py-6">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No hay presupuestos para este evento</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {presupuestos.map((presupuesto) => (
                      <Card key={presupuesto.id} className={`border-2 ${
                        presupuesto.estado === 'aceptado' 
                          ? 'bg-green-900/10 border-green-700/50' 
                          : presupuesto.estado === 'rechazado'
                          ? 'bg-red-900/10 border-red-700/50'
                          : 'bg-slate-700/30 border-slate-600'
                      }`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <CardTitle className="text-white text-lg">
                                  Presupuesto #{presupuesto.id.slice(0, 8).toUpperCase()}
                                </CardTitle>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  presupuesto.estado === 'aceptado' 
                                    ? 'bg-green-600 text-white' 
                                    : presupuesto.estado === 'rechazado'
                                    ? 'bg-red-600 text-white'
                                    : presupuesto.estado === 'visto'
                                    ? 'bg-purple-600 text-white'
                                    : presupuesto.estado === 'enviado'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-amber-600 text-white'
                                }`}>
                                  {presupuesto.estado === 'aceptado' ? '✓ Aceptado' : 
                                   presupuesto.estado === 'rechazado' ? '✗ Rechazado' :
                                   presupuesto.estado === 'visto' ? '👁️ Revisado' :
                                   presupuesto.estado === 'enviado' ? '📤 Enviado' :
                                   '⏳ Pendiente'}
                                </span>
                              </div>
                              {presupuesto.created_at && (
                                <p className="text-slate-400 text-sm mt-2">
                                  Fecha: {new Date(presupuesto.created_at).toLocaleDateString('es-ES')}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Servicios incluidos */}
                          <div className="space-y-2">
                            <p className="text-slate-400 text-sm font-semibold">Servicios incluidos:</p>
                            {presupuesto.servicios?.map((servicio, idx) => (
                              <div key={idx} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                                <span className="text-white">{servicio.nombre}</span>
                                <span className="text-emerald-400 font-semibold">
                                  {(servicio.precio * (servicio.cantidad || 1)).toFixed(2)}€
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Totales */}
                          <div className="p-4 bg-slate-800 rounded-lg space-y-2">
                            <div className="flex justify-between text-slate-300">
                              <span>Subtotal:</span>
                              <span>{presupuesto.subtotal?.toFixed(2) || '0.00'}€</span>
                            </div>
                            {presupuesto.descuento_porcentaje > 0 && (
                              <div className="flex justify-between text-amber-400">
                                <span>Descuento ({presupuesto.descuento_porcentaje}%):</span>
                                <span>-{presupuesto.total_descuento?.toFixed(2) || '0.00'}€</span>
                              </div>
                            )}
                            <div className="flex justify-between text-slate-300">
                              <span>IVA ({presupuesto.iva || 10}%):</span>
                              <span>+{presupuesto.total_iva?.toFixed(2) || '0.00'}€</span>
                            </div>
                            <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-600">
                              <span className="text-white">TOTAL:</span>
                              <span className="text-emerald-400">{presupuesto.total?.toFixed(2) || '0.00'}€</span>
                            </div>
                          </div>

                          {/* Notas */}
                          {presupuesto.notas && (
                            <div className="p-3 bg-slate-700/50 rounded-lg">
                              <p className="text-slate-400 text-sm font-semibold mb-1">Notas:</p>
                              <p className="text-slate-300 text-sm">{presupuesto.notas}</p>
                            </div>
                          )}

                          {/* Validez */}
                          <p className="text-slate-500 text-xs">
                            Validez: {presupuesto.validez_dias || 15} días
                          </p>

                          {/* Botones de acción */}
                          <div className="flex flex-wrap gap-2">
                            {/* Botón descargar PDF */}
                            <button
                              onClick={() => downloadPresupuestoPDF(presupuesto.id)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Descargar PDF
                            </button>

                            {/* Botones aceptar/rechazar (solo si está pendiente) */}
                            {!['aceptado', 'rechazado'].includes(presupuesto.estado) && (
                              <>
                                <button
                                  onClick={async () => {
                                    const nombre = prompt('Tu nombre para confirmar:');
                                    if (!nombre) return;
                                    try {
                                      const response = await fetch(`${API}/presupuestos/portal/${presupuesto.id}/respond`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ aceptar: true, nombre_firmante: nombre })
                                      });
                                      const data = await response.json();
                                      if (data.success) {
                                        alert('¡Presupuesto aceptado! Nos pondremos en contacto contigo.');
                                        loadPresupuestos(accessCode);
                                      }
                                    } catch (err) {
                                      alert('Error al aceptar');
                                    }
                                  }}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                  Aceptar
                                </button>
                                <button
                                  onClick={async () => {
                                    const motivo = prompt('Motivo del rechazo (opcional):');
                                    try {
                                      const response = await fetch(`${API}/presupuestos/portal/${presupuesto.id}/respond`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ aceptar: false, motivo_rechazo: motivo || '' })
                                      });
                                      const data = await response.json();
                                      if (data.success) {
                                        alert('Presupuesto rechazado. Gracias por tu respuesta.');
                                        loadPresupuestos(accessCode);
                                      }
                                    } catch (err) {
                                      alert('Error al rechazar');
                                    }
                                  }}
                                  className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-600 text-red-400 hover:bg-red-600/20 rounded-lg transition-colors"
                                >
                                  Rechazar
                                </button>
                              </>
                            )}
                          </div>

                          {/* Info si ya está aceptado */}
                          {presupuesto.estado === 'aceptado' && presupuesto.fecha_aceptado && (
                            <div className="p-3 bg-green-900/20 border border-green-700/50 rounded-lg">
                              <p className="text-green-300 text-sm">
                                ✓ Aceptado el {new Date(presupuesto.fecha_aceptado).toLocaleDateString('es-ES')}
                                {presupuesto.nombre_firmante && ` por ${presupuesto.nombre_firmante}`}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECCIÓN CONTRATOS */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-500" />
                  Contratos del Evento
                </CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Aquí puedes ver y firmar los contratos generados para tu evento
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {contratos.length === 0 ? (
                  <div className="text-center py-6">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No hay contratos generados para este evento</p>
                    <p className="text-slate-500 text-sm mt-2">
                      Los contratos aparecerán aquí una vez que el equipo los prepare
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contratos.map((contrato) => (
                      <Card key={contrato.id} className={`border-2 ${
                        contrato.estado === 'firmado' 
                          ? 'bg-green-900/10 border-green-700/50' 
                          : 'bg-slate-700/30 border-slate-600'
                      }`}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <CardTitle className="text-white text-lg">
                                  Contrato #{contrato.id.slice(0, 8).toUpperCase()}
                                </CardTitle>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  contrato.estado === 'firmado_ambos' 
                                    ? 'bg-green-600 text-white' 
                                    : contrato.estado === 'firmado_cliente'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-orange-600 text-white'
                                }`}>
                                  {contrato.estado === 'firmado_ambos' ? '✓✓ Firmado Completo' : 
                                   contrato.estado === 'firmado_cliente' ? '✓ Tu Firma Recibida' : 
                                   'Pendiente de Tu Firma'}
                                </span>
                              </div>
                              <p className="text-slate-400 text-sm mt-2">
                                Fecha de emisión: {new Date(contrato.fecha_generacion).toLocaleDateString('es-ES')}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Información del contrato */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-800/50 rounded-lg">
                            <div>
                              <p className="text-slate-400 text-sm">Cliente</p>
                              <p className="text-white font-medium">{contrato.cliente_nombre}</p>
                            </div>
                            <div>
                              <p className="text-slate-400 text-sm">DNI</p>
                              <p className="text-white font-medium">{contrato.cliente_dni}</p>
                            </div>
                            <div>
                              <p className="text-slate-400 text-sm">Precio Total</p>
                              <p className="text-white font-medium text-lg">{contrato.precio_total}€</p>
                            </div>
                            <div>
                              <p className="text-slate-400 text-sm">Anticipo</p>
                              <p className="text-cyan-400 font-medium">{contrato.anticipo || 0}€</p>
                            </div>
                          </div>

                          {/* Servicios contratados */}
                          <div>
                            <p className="text-slate-300 font-semibold mb-2">Servicios Contratados:</p>
                            <div className="space-y-2">
                              {contrato.servicios.map((servicio, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                                  <div>
                                    <p className="text-white text-sm font-medium">{servicio.nombre}</p>
                                    {servicio.descripcion && (
                                      <p className="text-slate-400 text-xs">{servicio.descripcion}</p>
                                    )}
                                  </div>
                                  <p className="text-cyan-400 font-semibold">{servicio.precio}€</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Información de firma */}
                          {(contrato.fecha_firma_cliente || contrato.fecha_firma_admin) && (
                            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg space-y-2">
                              <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-green-400 font-semibold mb-2">Estado de Firmas</p>
                                  
                                  {contrato.fecha_firma_cliente && (
                                    <div className="mb-2">
                                      <p className="text-slate-300 text-sm font-medium">✓ Tu firma:</p>
                                      <p className="text-slate-400 text-xs">
                                        Firmado el {new Date(contrato.fecha_firma_cliente).toLocaleString('es-ES')}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {contrato.fecha_firma_admin ? (
                                    <div>
                                      <p className="text-slate-300 text-sm font-medium">✓ Firma del proveedor:</p>
                                      <p className="text-slate-400 text-xs">
                                        Firmado el {new Date(contrato.fecha_firma_admin).toLocaleString('es-ES')}
                                      </p>
                                    </div>
                                  ) : contrato.fecha_firma_cliente && (
                                    <div>
                                      <p className="text-yellow-400 text-sm font-medium">⏳ Firma del proveedor:</p>
                                      <p className="text-slate-400 text-xs">Pendiente</p>
                                    </div>
                                  )}
                                  
                                  {contrato.estado === 'firmado_ambos' && (
                                    <div className="mt-2 pt-2 border-t border-green-700/30">
                                      <p className="text-green-300 text-sm font-semibold">
                                        ✓ Contrato completamente firmado y válido
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Botones de acción */}
                          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-700">
                            {/* Botón descargar PDF */}
                            <button
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${API}/contratos/${contrato.id}/pdf`);
                                  if (!response.ok) throw new Error('Error al descargar');
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `contrato_${contrato.id.slice(0, 8)}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (error) {
                                  console.error('Error downloading PDF:', error);
                                  alert('Error al descargar el contrato');
                                }
                              }}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Descargar PDF
                            </button>

                            {/* Botón firmar (solo si no está firmado por cliente) */}
                            {!contrato.fecha_firma_cliente && (
                              <button
                                onClick={() => handleFirmarContrato(contrato.id, contrato.cliente_nombre)}
                                disabled={saving}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors font-semibold"
                              >
                                {saving ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Firmando...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Firmar Contrato
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* Advertencia legal */}
                          {!contrato.fecha_firma_cliente && (
                            <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                              <p className="text-amber-300 text-xs">
                                ⚠️ Al firmar este contrato, acepta todos los términos y condiciones especificados en el documento PDF.
                                La firma electrónica tiene la misma validez legal que una firma manuscrita.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Documentos */}
        {activeTab === 'documentos' && (
          <div className="space-y-4">
            {/* Upload section */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Subir Documentos</CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Puedes subir logos, referencias, o cualquier documento relacionado con tu evento (máximo 10MB por archivo)
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleUploadDocument}
                    disabled={uploadingDoc}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-md cursor-pointer transition-colors ${
                      uploadingDoc ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Download className="w-5 h-5" />
                    {uploadingDoc ? 'Subiendo...' : 'Seleccionar Archivo'}
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Documents list */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Mis Documentos</CardTitle>
              </CardHeader>
              <CardContent>
                {!eventData?.documentos || eventData.documentos.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">
                    No hay documentos disponibles
                  </div>
                ) : (
                  <div className="space-y-2">
                    {eventData.documentos.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between bg-slate-700/50 p-3 rounded">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-sky-400" />
                          <div>
                            <div className="text-white font-medium">{doc.nombre}</div>
                            <div className="text-xs text-slate-400">
                              {doc.tipo} • Subido por: {doc.subido_por}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-sky-600 hover:bg-sky-700"
                          onClick={async () => {
                            try {
                              const response = await fetch(doc.url);
                              if (!response.ok) throw new Error('Error al descargar');
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.nombre || 'documento';
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (error) {
                              console.error('Error downloading document:', error);
                              // Fallback to opening in new tab if direct download fails
                              window.open(doc.url, '_blank');
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Descargar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reseñas */}
        {activeTab === 'resenas' && (
          <div className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  ¡Déjanos tu Reseña!
                </CardTitle>
                <p className="text-slate-400 text-sm mt-2">
                  Tu opinión es muy importante para nosotros. Si disfrutaste de nuestros servicios, por favor comparte tu experiencia.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Google Review */}
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/50 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-white rounded-lg p-3 flex-shrink-0">
                      <svg className="w-8 h-8" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-2">Google Reviews</h3>
                      <p className="text-slate-400 text-sm mb-4">
                        Comparte tu experiencia en Google y ayuda a otros a conocernos
                      </p>
                      <a
                        href="https://g.page/r/CQ_NEWsLl8qMEBM/review"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                          <Star className="w-4 h-4 mr-2" />
                          Dejar Reseña en Google
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Bodas.net Review */}
                <div className="bg-gradient-to-br from-pink-900/30 to-pink-800/20 border border-pink-700/50 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-white rounded-lg p-3 flex-shrink-0">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#E91E63"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-2">Bodas.net</h3>
                      <p className="text-slate-400 text-sm mb-4">
                        Deja tu opinión en Bodas.net y ayuda a futuras parejas a elegir
                      </p>
                      <a
                        href="https://www.bodas.net/shared/rate/69969"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <Button className="bg-pink-600 hover:bg-pink-700 text-white">
                          <Star className="w-4 h-4 mr-2" />
                          Dejar Reseña en Bodas.net
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Share */}
                <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/50 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-white rounded-lg p-3 flex-shrink-0">
                      <MessageCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-2">Comparte por WhatsApp</h3>
                      <p className="text-slate-400 text-sm mb-4">
                        ¿Tienes algo que contarnos directamente? Escríbenos por WhatsApp
                      </p>
                      <a
                        href="https://wa.me/34667472074?text=¡Hola! Quiero dejar mi opinión sobre el servicio de Luis Herrera Eventos."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <Button className="bg-green-600 hover:bg-green-700 text-white">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Contactar por WhatsApp
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="text-center text-slate-400 text-sm mt-6">
                  <p>¡Gracias por confiar en Luis Herrera Eventos! 🎉</p>
                  <p className="mt-1">Tu opinión nos ayuda a seguir mejorando</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="bg-slate-800 border-t border-slate-700 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-slate-400 text-sm">
          <p>Luis Herrera Eventos DJ TEAM</p>
          <p className="mt-1">www.luisherreraeventos.com</p>
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;
